import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import {
  ArrowLeft, Target, Clock, TrendingUp, Heart, MessageCircle,
  ChevronDown, ChevronUp, Sparkles, Check, Loader2, Mic, Square
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useVoiceInputWhisper } from "@/hooks/useVoiceInputWhisper";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Legacy key used before the mirror_context table existed. Kept here so the
// one-time migration prompt and the "already migrated" flag stay in sync.
const LEGACY_STORAGE_KEY = "vyv_mirror_context";
const MIGRATION_DONE_KEY = "vyv_mirror_context_migrated";

const TeachMirror = () => {
  const navigate = useNavigate();
  const { t, tf, tp, lang } = useLanguage();
  const [expandedId, setExpandedId] = useState<string | null>("goals");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  // Último valor CONFIRMADO no banco por seção — o Salvar só existe quando o
  // campo diverge dele (fim do botão desabilitado permanente, Rafa 25/08).
  const [lastSaved, setLastSaved] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [saveError, setSaveError] = useState<Record<string, boolean>>({});
  const [userId, setUserId] = useState<string | null>(null);
  const [shouldCheckImport, setShouldCheckImport] = useState(true);

  // Voz com o MESMO peso do teclado em todos os blocos (voice-first, Rafa
  // 25/08). Recorder canônico (fix iOS, amplitude, 402→PaywallSheet) — um
  // por tela, alvo = seção aberta; custo carimbado surface 'teach_mirror'
  // (countSurface segue 'stt', o contador dos rate-gates não forka nunca).
  const stt = useVoiceInputWhisper(lang, { surface: "teach_mirror" });
  const [voiceTarget, setVoiceTarget] = useState<string | null>(null);

  const sections = [
    {
      id: "goals", icon: Target, title: t("teach.goals"), subtitle: t("teach.goalsSubtitle"),
      prompts: [t("teach.goalsPrompt1"), t("teach.goalsPrompt2"), t("teach.goalsPrompt3")],
    },
    {
      id: "routine", icon: Clock, title: t("teach.routine"), subtitle: t("teach.routineSubtitle"),
      prompts: [t("teach.routinePrompt1"), t("teach.routinePrompt2"), t("teach.routinePrompt3")],
    },
    {
      id: "growth", icon: TrendingUp, title: t("teach.growth"), subtitle: t("teach.growthSubtitle"),
      prompts: [t("teach.growthPrompt1"), t("teach.growthPrompt2"), t("teach.growthPrompt3")],
    },
    {
      id: "support", icon: Heart, title: t("teach.support"), subtitle: t("teach.supportSubtitle"),
      prompts: [t("teach.supportPrompt1"), t("teach.supportPrompt2"), t("teach.supportPrompt3")],
    },
    {
      id: "about", icon: MessageCircle, title: t("teach.about"), subtitle: t("teach.aboutSubtitle"),
      prompts: [t("teach.aboutPrompt1"), t("teach.aboutPrompt2")],
    },
  ];

  // Load context rows from the mirror_context table (replaces legacy
  // localStorage read). Casts to (supabase as any) because the generated
  // types.ts may not yet include mirror_context — same pattern used by
  // useBrain, useXP, and useAvatarSnapshot elsewhere in the codebase.
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        if (cancelled) return;
        setUserId(user.id);

        const { data, error } = await (supabase as any)
          .from("mirror_context")
          .select("section, content")
          .eq("user_id", user.id);

        if (cancelled) return;

        if (error) {
          console.error("[TeachMirror] load error:", error);
          toast.error(t("common.error"));
          return;
        }

        const map: Record<string, string> = {};
        (data as Array<{ section: string; content: string }> | null)?.forEach((row) => {
          if (row?.section && typeof row.content === "string") {
            map[row.section] = row.content;
          }
        });
        setAnswers(map);
        setLastSaved(map);

        // One-time migration: if the DB has no rows AND the legacy localStorage
        // key still has data AND we haven't already offered the prompt, ask the
        // user whether to import.
        if (shouldCheckImport) {
          setShouldCheckImport(false);
          const dbEmpty = !data || (data as unknown[]).length === 0;
          const alreadyMigrated = localStorage.getItem(MIGRATION_DONE_KEY) === "true";
          if (dbEmpty && !alreadyMigrated) {
            const legacy = readLegacyStorage();
            if (legacy) {
              promptImport(user.id, legacy);
            } else {
              // No legacy data either — nothing to migrate, mark done so we
              // don't re-check every mount.
              localStorage.setItem(MIGRATION_DONE_KEY, "true");
            }
          }
        }
      } catch (err) {
        console.error("[TeachMirror] load threw:", err);
      }
    };

    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const readLegacyStorage = (): Record<string, string> | null => {
    try {
      const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      const entries = Object.entries(parsed).filter(
        ([, v]) => typeof v === "string" && (v as string).trim().length > 0
      ) as Array<[string, string]>;
      if (entries.length === 0) return null;
      return Object.fromEntries(entries);
    } catch {
      return null;
    }
  };

  const promptImport = (uid: string, legacy: Record<string, string>) => {
    const count = Object.keys(legacy).length;
    const title = t("teach.import.title");
    const description =
      t("teach.import.descPrefix") +
      count +
      tp("teach.import.section", count) +
      t("teach.import.descSuffix");

    toast(title, {
      description,
      duration: 120000,
      action: {
        label: t("teach.import.yes"),
        onClick: () => {
          void (async () => {
            const results = await Promise.allSettled(
              Object.entries(legacy).map(([section, content]) =>
                (supabase as any)
                  .from("mirror_context")
                  .upsert(
                    {
                      user_id: uid,
                      section,
                      content,
                      updated_at: new Date().toISOString(),
                    },
                    { onConflict: "user_id,section" }
                  )
                  .select()
              )
            );
            const ok = results.filter((r) => {
              if (r.status !== "fulfilled") return false;
              const v = r.value as any;
              if (v?.error) return false;
              if (!Array.isArray(v?.data) || v.data.length === 0) return false;
              return true;
            }).length;
            setAnswers((prev) => ({ ...prev, ...legacy }));
            setLastSaved((prev) => ({ ...prev, ...legacy }));
            localStorage.removeItem(LEGACY_STORAGE_KEY);
            localStorage.setItem(MIGRATION_DONE_KEY, "true");
            toast.success(
              ok +
                (ok === 1
                  ? t("teach.import.successSing")
                  : t("teach.import.successPlur"))
            );
          })();
        },
      },
      cancel: {
        label: t("teach.import.no"),
        onClick: () => {
          localStorage.removeItem(LEGACY_STORAGE_KEY);
          localStorage.setItem(MIGRATION_DONE_KEY, "true");
        },
      },
    });
  };

  const toggle = (id: string) => setExpandedId(prev => (prev === id ? null : id));

  const handleSave = async (id: string) => {
    const content = answers[id]?.trim();
    if (!content) return;

    // Re-fetch session right before save to ensure we have a live JWT.
    // Cached userId from useEffect mount may be stale (token rotated, sign-out elsewhere).
    const { data: { user: liveUser }, error: liveUserErr } = await supabase.auth.getUser();
    if (liveUserErr || !liveUser) {
      console.error("[TeachMirror] save aborted — no live session", { liveUserErr, hadCachedId: !!userId });
      setSaveError(prev => ({ ...prev, [id]: true }));
      setTimeout(() => setSaveError(prev => ({ ...prev, [id]: false })), 3000);
      return;
    }

    const effectiveUserId = liveUser.id;
    if (userId && userId !== effectiveUserId) {
      console.warn("[TeachMirror] cached userId differs from live session", { cached: userId, live: effectiveUserId });
    }

    setSaving(prev => ({ ...prev, [id]: true }));

    try {
      // .select() forces PostgREST to return the inserted/updated rows.
      // Without it, RLS-blocked-as-noop returns success with 0 rows and
      // we cannot distinguish a real save from a silent failure.
      const { data, error, status, statusText } = await (supabase as any)
        .from("mirror_context")
        .upsert(
          {
            user_id: effectiveUserId,
            section: id,
            content,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,section" }
        )
        .select();

      console.log("[TeachMirror] save response", {
        section: id,
        effectiveUserId: effectiveUserId.slice(0, 8),
        status,
        statusText,
        hasError: !!error,
        errorCode: error?.code,
        errorMsg: error?.message,
        rowsReturned: Array.isArray(data) ? data.length : "not-array",
      });

      if (error) {
        console.error("[TeachMirror] save error from PostgREST:", error);
        setSaving(prev => ({ ...prev, [id]: false }));
        setSaveError(prev => ({ ...prev, [id]: true }));
        setTimeout(() => setSaveError(prev => ({ ...prev, [id]: false })), 3000);
        return;
      }

      if (!Array.isArray(data) || data.length === 0) {
        // Critical: PostgREST said OK but no rows came back. RLS silently
        // blocked the write OR the row didn't pass with_check. This is the
        // exact failure mode we hit before — surface it instead of lying.
        console.error("[TeachMirror] save returned 0 rows — likely RLS block. effectiveUserId:", effectiveUserId);
        toast.error(t("teach.saveError") || t("common.error"));
        setSaving(prev => ({ ...prev, [id]: false }));
        setSaveError(prev => ({ ...prev, [id]: true }));
        setTimeout(() => setSaveError(prev => ({ ...prev, [id]: false })), 5000);
        return;
      }

      // Genuine success: PostgREST returned at least one row.
      console.log("[TeachMirror] save confirmed", { rowId: data[0]?.id, section: data[0]?.section });
      setAnswers(prev => ({ ...prev, [id]: content }));
      setLastSaved(prev => ({ ...prev, [id]: content }));
      setSaving(prev => ({ ...prev, [id]: false }));
      setSaved(prev => ({ ...prev, [id]: true }));
      setTimeout(() => setSaved(prev => ({ ...prev, [id]: false })), 2000);
    } catch (err) {
      console.error("[TeachMirror] save threw:", err);
      setSaving(prev => ({ ...prev, [id]: false }));
      setSaveError(prev => ({ ...prev, [id]: true }));
      setTimeout(() => setSaveError(prev => ({ ...prev, [id]: false })), 3000);
    }
  };

  // ── Voz — um recorder, alvo = a seção onde o mic foi tocado ────────────
  const startVoice = async (target: string) => {
    if (stt.isListening || stt.isTranscribing) return;
    setVoiceTarget(target);
    try {
      await stt.startListening();
    } catch (err) {
      console.error("[TeachMirror] mic start failed:", err);
      setVoiceTarget(null);
    }
  };

  // Sem useCallback de propósito: recriada a cada render, fecha sobre o
  // answers CORRENTE — nada de escrever variável de fora dentro de updater
  // (§11, pureUpdater). O transcript cai no MESMO campo do teclado.
  const stopVoice = async () => {
    const target = voiceTarget;
    try {
      const text = (await stt.stopListening()).trim();
      if (!text || !target) return;
      const current = answers[target]?.trim() ?? "";
      const merged = current ? current + " " + text : text;
      setAnswers(prev => ({ ...prev, [target]: merged }));
    } finally {
      setVoiceTarget(null);
    }
  };

  const micButton = (target: string) => {
    if (!stt.supported) return null;
    const activeHere = voiceTarget === target;
    const listening = stt.isListening;
    const transcribing = stt.isTranscribing;
    const busyElsewhere = (listening || transcribing) && !activeHere;
    return (
      <button
        type="button"
        disabled={busyElsewhere}
        onClick={() => (activeHere && listening ? void stopVoice() : void startVoice(target))}
        aria-label={activeHere && listening ? t("teach.voice.stop") : t("teach.voice.speak")}
        className="w-[52px] shrink-0 rounded-xl inline-flex flex-col items-center justify-center gap-1 font-display text-[9px] font-semibold tracking-wide transition-all active:scale-95 disabled:opacity-35"
        style={{
          background:
            activeHere && listening ? "hsl(0 70% 55% / 0.18)" : "hsl(187 72% 50% / 0.12)",
          border:
            activeHere && listening
              ? "1px solid hsl(0 70% 55% / 0.55)"
              : "1px solid hsl(187 72% 50% / 0.35)",
          color: activeHere && listening ? "hsl(0 80% 72%)" : "hsl(187 72% 65%)",
          boxShadow:
            activeHere && listening
              ? "0 0 18px hsl(0 70% 55% / " + (0.15 + stt.amplitude * 0.35) + ")"
              : "none",
        }}
      >
        {activeHere && transcribing ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : activeHere && listening ? (
          <Square className="w-4 h-4" />
        ) : (
          <Mic className="w-4 h-4" />
        )}
        <span>
          {activeHere && transcribing
            ? t("teach.voice.transcribing")
            : activeHere && listening
              ? String(stt.recordingSeconds) + "s"
              : t("teach.voice.speak")}
        </span>
      </button>
    );
  };

  const filledCount = Object.values(answers).filter(v => v.trim()).length;

  // Divergiu do que está no banco? Só então o Salvar existe — o botão
  // desabilitado permanente era o elemento mais pesado da tela (Rafa 25/08).
  const isDirty = (id: string) =>
    (answers[id]?.trim() ?? "") !== (lastSaved[id]?.trim() ?? "");

  const buttonLabel = (id: string) => {
    if (saving[id]) return t("teach.saving");
    if (saved[id]) return t("teach.saved");
    if (saveError[id]) return t("teach.saveError");
    return t("teach.save");
  };

  const buttonClass = (id: string) => {
    if (saved[id]) return "bg-primary/15 text-primary border border-primary/20";
    if (saveError[id]) return "bg-destructive/10 text-destructive border border-destructive/20";
    if (saving[id]) return "bg-foreground/[0.04] text-muted-foreground border border-foreground/[0.08] cursor-wait";
    if (answers[id]?.trim()) return "bg-primary/10 text-primary/70 border border-primary/15 hover:bg-primary/20";
    return "bg-foreground/[0.03] text-muted-foreground/30 border border-foreground/[0.06] cursor-not-allowed";
  };

  return (
    <MobileLayout className="pb-12 pt-6 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[10%] left-1/2 -translate-x-1/2 w-[380px] h-[280px] rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }} />
      </div>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="flex items-center gap-3 mb-6 relative z-10">
        <button onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl bg-foreground/[0.04] border border-foreground/[0.08] flex items-center justify-center hover:bg-foreground/[0.08] transition-all duration-300">
          <ArrowLeft size={15} className="text-muted-foreground" />
        </button>
        <div>
          <h1 className="font-display text-[22px] font-semibold text-foreground leading-none">{t("teach.title")}</h1>
          <p className="text-muted-foreground text-[11px] mt-1 font-body">{t("teach.subtitle")}</p>
        </div>
      </motion.div>

      {/* Progresso SEGMENTADO (um segmento por área) + frase humana — o
          "0/5" e a barra contínua não comunicavam O QUE eram os 5. */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.5 }}
        className="mb-6 px-1">
        <div className="flex items-center gap-1.5">
          {sections.map((s, si) => (
            <motion.div
              key={s.id}
              className="flex-1 h-[4px] rounded-full"
              initial={false}
              animate={{
                background:
                  si < filledCount ? "hsl(187 72% 50% / 0.55)" : "rgba(255,255,255,0.06)",
              }}
              transition={{ duration: 0.4 }}
            />
          ))}
        </div>
        <p className="text-[10px] font-display text-muted-foreground tracking-wide mt-1.5">
          {tf("teach.progressLabel", {
            filled: String(filledCount),
            total: String(sections.length),
          })}
        </p>
      </motion.div>

      <div className="space-y-3 relative z-10">
        {sections.map((section, i) => {
          const isOpen = expandedId === section.id;
          const Icon = section.icon;
          const hasAnswer = !!answers[section.id]?.trim();

          return (
            <motion.div key={section.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + i * 0.07, duration: 0.5 }}
              className={`vyv-card overflow-hidden transition-all duration-300 ${isOpen ? "border-primary/15" : ""}`}>
              <button onClick={() => toggle(section.id)} className="w-full flex items-center gap-3 p-4 text-left">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 transition-all duration-300 ${
                  hasAnswer ? "bg-primary/15 border border-primary/20" : "bg-foreground/[0.04] border border-foreground/[0.06]"
                }`}>
                  {hasAnswer ? <Check size={14} className="text-primary/70" /> : <Icon size={14} className="text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-[14px] font-semibold text-foreground leading-snug">{section.title}</p>
                  <p className="text-muted-foreground text-[11px] mt-0.5 font-body truncate">{section.subtitle}</p>
                </div>
                {isOpen ? <ChevronUp size={14} className="text-muted-foreground/30 shrink-0" /> : <ChevronDown size={14} className="text-muted-foreground/30 shrink-0" />}
              </button>

              <AnimatePresence>
                {isOpen && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
                    <div className="px-4 pb-4 pt-0">
                      {/* Guia com identidade própria (deixou de parecer
                          placeholder): moldura sutil + micro-rótulo no
                          idioma da casa. */}
                      <div
                        className="rounded-xl px-3 py-2.5 mb-3"
                        style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)" }}
                      >
                        <p
                          className="text-[9px] font-display tracking-[0.22em] uppercase mb-1.5"
                          style={{ color: "hsl(187 72% 50% / 0.6)" }}
                        >
                          {t("teach.promptsLabel")}
                        </p>
                        <div className="space-y-1.5">
                          {section.prompts.map((prompt, pi) => (
                            <div key={pi} className="flex items-start gap-2">
                              <span className="w-1 h-1 rounded-full shrink-0 mt-[6px]" style={{ background: "hsl(187 72% 50% / 0.45)" }} />
                              <p className="text-muted-foreground text-[11.5px] font-body leading-relaxed">{prompt}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                      {/* Falar OU escrever, mesmo peso — o mic mora ao lado
                          do campo, como no restante do app. O transcript cai
                          NESTE campo. */}
                      <div className="flex items-stretch gap-2.5">
                        <Textarea value={answers[section.id] || ""} onChange={(e) => setAnswers(prev => ({ ...prev, [section.id]: e.target.value }))}
                          placeholder={t("teach.sharePlaceholder")}
                          className="flex-1 min-h-[80px] bg-foreground/[0.03] border-foreground/[0.08] text-[12px] font-body text-foreground/80 placeholder:text-muted-foreground/30 rounded-xl resize-none focus:border-primary/20 focus:ring-primary/10 transition-all duration-300" />
                        {micButton(section.id)}
                      </div>
                      {/* Salvar só EXISTE quando o campo diverge do que está
                          gravado (ou durante o feedback de salvando/salvo/
                          erro) — o desabilitado permanente saiu. */}
                      {((isDirty(section.id) && !!answers[section.id]?.trim()) || saving[section.id] || saved[section.id] || saveError[section.id]) && (
                        <div className="flex justify-end mt-3">
                          <button
                            onClick={() => handleSave(section.id)}
                            disabled={!answers[section.id]?.trim() || saving[section.id]}
                            className={`px-4 py-2 rounded-xl text-[11px] font-display font-medium tracking-wide transition-all duration-300 ${buttonClass(section.id)}`}
                          >
                            {buttonLabel(section.id)}
                          </button>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.6 }}
        className="flex items-center justify-center gap-2 mt-8 mb-4">
        <Sparkles size={9} className="text-primary/25" />
        <p className="text-muted-foreground text-[10px] font-display tracking-wide">{t("teach.footer")}</p>
        <Sparkles size={9} className="text-primary/25" />
      </motion.div>
    </MobileLayout>
  );
};

export default TeachMirror;
